import { z } from "zod";
import { optNum } from "./zodNum";
import { isoDate } from "./dates";

export const VAT_CODES = [
  { code: "27", label: "27%" },
  { code: "18", label: "18%" },
  { code: "5", label: "5%" },
  { code: "0", label: "0%" },
  { code: "TAM", label: "Tárgyi adómentes (TAM)" },
  { code: "AAM", label: "Alanyi adómentes (AAM)" },
  { code: "EU", label: "EU-n belüli (EU)" },
  { code: "EUK", label: "EU-n kívüli (EUK)" },
  { code: "MAA", label: "Mentes az adó alól (MAA)" },
] as const;

export const CHARGE_KINDS = ["RENT", "SERVICE_CHARGE", "PARKING", "STORAGE", "UTILITY", "REG_OFFICE", "FITOUT", "FITOUT_CREDIT", "MARKETING", "OTHER"] as const;
export type ChargeKind = (typeof CHARGE_KINDS)[number];
export const CHARGE_BASES = ["PER_MONTH", "PER_SQM_MONTH", "PER_PIECE_MONTH", "PER_YEAR", "PER_SQM_YEAR", "ONE_OFF"] as const;
export type ChargeBasisCode = (typeof CHARGE_BASES)[number];

/** Fizetési határidő szabálya: a kelttől számított napok, vagy a kelt hónapjának utolsó napja. */
export const DUE_RULES = ["DAYS_AFTER_ISSUE", "END_OF_ISSUE_MONTH"] as const;
export type DueRule = (typeof DUE_RULES)[number];
export const DUE_RULE_LABEL: Record<DueRule, string> = {
  DAYS_AFTER_ISSUE: "A kelttől számított napok",
  END_OF_ISSUE_MONTH: "A kelt hónapjának utolsó napja",
};
/** A számlán választható fizetési módok (a számlázz.hu is ezeket a szövegeket várja). */
export const PAYMENT_METHODS = ["Átutalás", "Készpénz", "Bankkártya"] as const;

export const CHARGE_KIND_LABEL: Record<ChargeKind, string> = {
  RENT: "Bérleti díj",
  SERVICE_CHARGE: "Üzemeltetési díj",
  PARKING: "Parkoló",
  STORAGE: "Raktár",
  UTILITY: "Közüzem",
  REG_OFFICE: "Székhelyszolgáltatás",
  FITOUT: "Kialakítás részlet",
  FITOUT_CREDIT: "Kialakítás beszámítás",
  MARKETING: "Marketing hozzájárulás",
  OTHER: "Egyéb",
};

export const CHARGE_BASIS_LABEL: Record<ChargeBasisCode, string> = {
  PER_MONTH: "/ hó",
  PER_SQM_MONTH: "/ m² / hó",
  PER_PIECE_MONTH: "/ db / hó",
  PER_YEAR: "/ év",
  PER_SQM_YEAR: "/ m² / év",
  ONE_OFF: "egyszeri",
};

export const DEFAULT_CHARGE_TYPES: { key: string; label: string; kind: ChargeKind; defaultBasis: ChargeBasisCode; indexableDefault: boolean }[] = [
  { key: "rent", label: "Bérleti díj", kind: "RENT", defaultBasis: "PER_MONTH", indexableDefault: true },
  { key: "service_flat", label: "Üzemeltetési díj (átalány)", kind: "SERVICE_CHARGE", defaultBasis: "PER_MONTH", indexableDefault: true },
  { key: "service_on_account", label: "Üzemeltetési előleg", kind: "SERVICE_CHARGE", defaultBasis: "PER_SQM_MONTH", indexableDefault: true },
  { key: "parking", label: "Parkolóhely", kind: "PARKING", defaultBasis: "PER_PIECE_MONTH", indexableDefault: true },
  { key: "storage", label: "Raktár díj", kind: "STORAGE", defaultBasis: "PER_SQM_MONTH", indexableDefault: true },
  { key: "utility", label: "Közüzem továbbszámlázás", kind: "UTILITY", defaultBasis: "PER_MONTH", indexableDefault: false },
  { key: "reg_office", label: "Székhelyszolgáltatás", kind: "REG_OFFICE", defaultBasis: "PER_YEAR", indexableDefault: false },
  { key: "fitout", label: "Kialakítási részlet", kind: "FITOUT", defaultBasis: "PER_MONTH", indexableDefault: false },
  { key: "fitout_credit", label: "Kialakítás beszámítás", kind: "FITOUT_CREDIT", defaultBasis: "PER_MONTH", indexableDefault: false },
  { key: "property_tax", label: "Építményadó (áthárított)", kind: "OTHER", defaultBasis: "PER_YEAR", indexableDefault: false },
  { key: "other_service", label: "Egyedi szolgáltatás", kind: "OTHER", defaultBasis: "PER_MONTH", indexableDefault: false },
];

export const COST_GROUPS = [
  "UTILITIES",
  "CLEANING",
  "WASTE",
  "SECURITY",
  "LANDSCAPING",
  "MAINTENANCE",
  "FIRE_SAFETY",
  "INSURANCE",
  "PROPERTY_TAX",
  "MANAGEMENT_FEE",
  "REPAIRS",
  "OTHER",
  "STRUCTURAL_CAPEX",
  "NON_RECOVERABLE",
] as const;
export type CostGroupCode = (typeof COST_GROUPS)[number];
export const ALLOCATION_KEYS = ["LETTABLE_AREA", "OCCUPIED_AREA", "METERED", "FIXED_SHARE", "PER_UNIT"] as const;
export type AllocationKeyCode = (typeof ALLOCATION_KEYS)[number];

export const ALLOCATION_KEY_LABEL: Record<AllocationKeyCode, string> = {
  LETTABLE_AREA: "Bérbe adható terület arányában",
  OCCUPIED_AREA: "Bérbe adott terület arányában",
  METERED: "Fogyasztás szerint (mérőóra)",
  FIXED_SHARE: "Fix arány / összeg",
  PER_UNIT: "Darabszám szerint",
};

export const DEFAULT_COST_CATEGORIES: {
  key: string;
  label: string;
  group: CostGroupCode;
  recoverableDefault: boolean;
  allocationKeyDefault: AllocationKeyCode;
  isVariable: boolean;
  isMgmtFeeBase: boolean;
}[] = [
  { key: "common_utilities", label: "Közüzem közös területre", group: "UTILITIES", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: true, isMgmtFeeBase: false },
  { key: "cleaning", label: "Takarítás", group: "CLEANING", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: true, isMgmtFeeBase: true },
  { key: "waste", label: "Hulladékszállítás", group: "WASTE", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: true, isMgmtFeeBase: true },
  { key: "security", label: "Őrzés és portaszolgálat", group: "SECURITY", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "landscaping", label: "Kertészet és hóeltakarítás", group: "LANDSCAPING", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "lift_hvac", label: "Lift és gépészet karbantartása", group: "MAINTENANCE", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "fire_safety", label: "Tűz- és munkavédelem", group: "FIRE_SAFETY", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "insurance", label: "Épületbiztosítás", group: "INSURANCE", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "property_tax", label: "Építményadó és telekadó", group: "PROPERTY_TAX", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "management_fee", label: "Management díj", group: "MANAGEMENT_FEE", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "repairs", label: "Kisebb javítások", group: "REPAIRS", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "other_opex", label: "Egyéb üzemeltetés", group: "OTHER", recoverableDefault: true, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: true },
  { key: "structural_capex", label: "Szerkezeti felújítás (CAPEX)", group: "STRUCTURAL_CAPEX", recoverableDefault: false, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "leasing_commission", label: "Bérbeadási jutalék", group: "NON_RECOVERABLE", recoverableDefault: false, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "vacancy_marketing", label: "Üres területek marketingje", group: "NON_RECOVERABLE", recoverableDefault: false, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
  { key: "financing", label: "Bérbeadó finanszírozási költsége", group: "NON_RECOVERABLE", recoverableDefault: false, allocationKeyDefault: "LETTABLE_AREA", isVariable: false, isMgmtFeeBase: false },
];

export const COST_GROUP_LABEL: Record<CostGroupCode, string> = {
  UTILITIES: "Közüzem",
  CLEANING: "Takarítás",
  WASTE: "Hulladék",
  SECURITY: "Őrzés",
  LANDSCAPING: "Kertészet",
  MAINTENANCE: "Karbantartás",
  FIRE_SAFETY: "Tűzvédelem",
  INSURANCE: "Biztosítás",
  PROPERTY_TAX: "Ingatlanadó",
  MANAGEMENT_FEE: "Management díj",
  REPAIRS: "Javítások",
  OTHER: "Egyéb",
  STRUCTURAL_CAPEX: "Szerkezeti CAPEX",
  NON_RECOVERABLE: "Nem áthárítható",
};

export const LEASE_USAGES = ["OFFICE", "STORAGE", "RETAIL", "WORKSHOP", "PARKING", "REG_OFFICE", "OTHER"] as const;
export const LEASE_USAGE_LABEL: Record<(typeof LEASE_USAGES)[number], string> = {
  OFFICE: "Iroda",
  STORAGE: "Raktár",
  RETAIL: "Üzlet",
  WORKSHOP: "Üzem",
  PARKING: "Parkoló",
  REG_OFFICE: "Székhely",
  OTHER: "Egyéb",
};

export const SERVICE_CHARGE_MODE_LABEL = { NONE: "Nincs (bruttó díj)", FLAT: "Átalány", ON_ACCOUNT: "Előleg + éves elszámolás" } as const;
export const OPTION_KIND_LABEL = { RENEWAL: "Hosszabbítás", BREAK: "Kilépés (break)", EXPANSION: "Bővítés", PURCHASE: "Vételi jog" } as const;
export const SECURITY_KIND_LABEL = { DEPOSIT: "Óvadék", BANK_GUARANTEE: "Bankgarancia", PARENT_GUARANTEE: "Anyavállalati garancia", OTHER: "Egyéb biztosíték" } as const;

const money = z.coerce.number({ invalid_type_error: "Számot adj meg" }).min(-1e12).max(1e12);
const optMoney = optNum(money);
const optStr = (max = 200) => z.string().trim().max(max).nullable().optional();
const currency = z.string().trim().toUpperCase().length(3).default("HUF");

export const legalEntitySchema = z.object({
  name: z.string().trim().min(2).max(200),
  shortName: optStr(60),
  taxNumber: optStr(30),
  euVatNumber: optStr(30),
  companyRegNo: optStr(40),
  zip: optStr(12),
  city: optStr(120),
  street: optStr(200),
  email: z.string().trim().toLowerCase().email().nullable().optional().or(z.literal("")),
  phone: optStr(40),
  vatMode: z.enum(["OPTED_TAXABLE", "EXEMPT"]).default("OPTED_TAXABLE"),
  isManagerEntity: z.boolean().default(false),
  managerMayIssue: z.boolean().default(true),
  paymentConfirmByManager: z.boolean().default(true),
  paymentConfirmByOwner: z.boolean().default(true),
  isGroupMember: z.boolean().default(false),
  groupName: optStr(120),
  invoicePrefix: optStr(20),
});

export const bankAccountSchema = z.object({
  bankName: optStr(120),
  accountNumber: z.string().trim().min(8).max(40),
  iban: optStr(40),
  swift: optStr(15),
  currency,
  isDefault: z.boolean().default(false),
});

export const ownershipSchema = z.object({ effectiveDate: isoDate, spaceIds: z.array(z.string().uuid()).min(1), legalEntityId: z.string().uuid() });

export const tenantSchema = z.object({
  kind: z.enum(["COMPANY", "PERSON"]).default("COMPANY"),
  name: z.string().trim().min(2).max(200),
  shortName: optStr(60),
  taxNumber: optStr(30),
  euVatNumber: optStr(30),
  registrationNo: optStr(40),
  billingZip: optStr(12),
  billingCity: optStr(120),
  billingStreet: optStr(200),
  email: z.string().trim().toLowerCase().email().nullable().optional().or(z.literal("")),
  phone: optStr(40),
  groupName: optStr(120),
  isRelatedParty: z.boolean().default(false),
  parentTenantId: z.string().uuid().nullable().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  note: optStr(2000),
});

export const tenantContactSchema = z.object({
  name: z.string().trim().min(2).max(120),
  role: optStr(80),
  email: z.string().trim().toLowerCase().email().nullable().optional().or(z.literal("")),
  phone: optStr(40),
  isPrimary: z.boolean().default(false),
  receivesInvoices: z.boolean().default(false),
});

export const leaseIdentitySchema = z.object({
  signedAt: isoDate.nullable().optional(),
  usage: z.enum(LEASE_USAGES).optional(),
  isIntercompany: z.boolean().optional(),
  externalRef: optStr(80),
  externalBilling: z.boolean().optional(),
  note: optStr(4000),
});

export const leaseTermsSchema = z.object({
  isFixedTerm: z.boolean().default(true),
  expiryDate: isoDate.nullable().optional(),
  noticeMonths: optNum(z.coerce.number().int().min(0).max(120)),
  billingFrequency: z.coerce.number().int().refine((v) => [1, 3, 6, 12].includes(v), "1, 3, 6 vagy 12 hónap").default(1),
  billingMonthOffset: z.coerce.number().int().min(-2).max(2).default(0),
  billingDay: z.coerce.number().int().min(1).max(28).default(1),
  paymentTermDays: z.coerce.number().int().min(0).max(120).default(8),
  dueRule: z.enum(DUE_RULES).default("DAYS_AFTER_ISSUE"),
  paymentMethod: z.string().trim().min(2).max(40).default("Átutalás"),
  inAdvance: z.boolean().default(true),
  contractCurrency: currency,
  invoiceCurrency: currency,
  fxSource: z.enum(["MNB", "BANK", "FIXED"]).default("MNB"),
  fxDateRule: z.enum(["INVOICE_DATE", "FULFILMENT_DATE", "MONTH_FIRST"]).default("FULFILMENT_DATE"),
  fixedFxRate: optMoney,
  serviceChargeMode: z.enum(["NONE", "FLAT", "ON_ACCOUNT"]).default("FLAT"),
  contractualArea: optMoney,
  lateInterestText: optStr(300),
  auditRightDays: optNum(z.coerce.number().int().min(0).max(3650)),
  reconciliationDeadline: optStr(40),
  grossUpPct: optNum(z.coerce.number().min(0).max(100)),
  ervPerSqm: optMoney,
  subletAllowed: z.boolean().default(false),
  specialTerms: optStr(4000),
});
export type LeaseTermsInput = z.infer<typeof leaseTermsSchema>;

/** Feltételek módosítása: vagy javítás (ugyanarra a napra), vagy szerződésmódosítás új hatálynappal. */
export const reviseTermsSchema = z.object({
  effectiveDate: isoDate,
  terms: leaseTermsSchema.partial(),
  amendment: z.object({ title: z.string().trim().min(2).max(200), signedAt: isoDate.nullable().optional(), description: optStr(4000) }).optional(),
});

export const chargeSchema = z.object({
  chargeTypeId: z.string().uuid(),
  label: optStr(200),
  amount: money,
  currency,
  basis: z.enum(CHARGE_BASES),
  quantity: optMoney,
  dueMonth: optNum(z.coerce.number().int().min(1).max(12)),
  indexable: z.boolean().default(true),
  vatCode: z.string().trim().max(10).default("27"),
  note: optStr(1000),
});
export type ChargeInputShared = z.infer<typeof chargeSchema>;

export const addChargeSchema = z.object({ effectiveDate: isoDate, endDate: isoDate.nullable().optional(), charge: chargeSchema, amendmentId: z.string().uuid().optional() });
export const reviseChargeSchema = z.object({ effectiveDate: isoDate, charge: chargeSchema.partial(), amendmentId: z.string().uuid().optional() });
export const endChargeSchema = z.object({ effectiveDate: isoDate });

export const optionSchema = z.object({
  kind: z.enum(["RENEWAL", "BREAK", "EXPANSION", "PURCHASE"]),
  exerciseDeadline: isoDate.nullable().optional(),
  effectiveDate: isoDate.nullable().optional(),
  termMonths: optNum(z.coerce.number().int().min(0).max(600)),
  fee: optMoney,
  feeCurrency: z.string().trim().toUpperCase().length(3).nullable().optional(),
  note: optStr(1000),
  exercisedAt: isoDate.nullable().optional(),
  waivedAt: isoDate.nullable().optional(),
});

export const securitySchema = z.object({
  kind: z.enum(["DEPOSIT", "BANK_GUARANTEE", "PARENT_GUARANTEE", "OTHER"]),
  amount: money.refine((v) => v >= 0, "Nem lehet negatív"),
  currency,
  issuer: optStr(160),
  reference: optStr(80),
  receivedAt: isoDate.nullable().optional(),
  expiresAt: isoDate.nullable().optional(),
  topUpRequired: z.boolean().default(false),
  interestBearing: z.boolean().default(false),
  returnedAt: isoDate.nullable().optional(),
  note: optStr(1000),
});

export const recoverabilityRowSchema = z.object({
  costCategoryId: z.string().uuid(),
  recoverable: z.boolean(),
  allocationKey: z.enum(ALLOCATION_KEYS),
  fixedSharePct: optNum(z.coerce.number().min(0).max(100)),
  capPct: optNum(z.coerce.number().min(0).max(100)),
  mgmtFeePct: optNum(z.coerce.number().min(0).max(100)),
  grossUp: z.boolean().default(false),
});
export const recoverabilitySchema = z.object({ effectiveDate: isoDate, rows: z.array(recoverabilityRowSchema) });
export const applyModelSchema = z.object({ effectiveDate: isoDate, model: z.enum(["GROSS", "SINGLE_NET", "DOUBLE_NET", "TRIPLE_NET", "ABSOLUTE_NET"]) });

export const chargeTypeSchema = z.object({
  label: z.string().trim().min(1).max(120),
  kind: z.enum(CHARGE_KINDS),
  defaultBasis: z.enum(CHARGE_BASES),
  defaultVatCode: z.string().trim().max(10),
  indexableDefault: z.boolean(),
  commercialCode: optStr(40),
  glAccount: optStr(20),
  unit: optStr(20),
});

export const costCategorySchema = z.object({
  label: z.string().trim().min(1).max(120),
  group: z.enum(COST_GROUPS),
  recoverableDefault: z.boolean(),
  allocationKeyDefault: z.enum(ALLOCATION_KEYS),
  isVariable: z.boolean(),
  isMgmtFeeBase: z.boolean(),
  commercialCode: optStr(40),
  glAccount: optStr(20),
});

export const alertSettingsSchema = z.object({
  expiryDays: z.coerce.number().int().min(0).max(730).default(180),
  noticeDays: z.coerce.number().int().min(0).max(365).default(60),
  optionDays: z.coerce.number().int().min(0).max(365).default(60),
  guaranteeDays: z.coerce.number().int().min(0).max(365).default(45),
});

// ─── Szerződés dokumentumai ───
export const LEASE_DOCUMENT_KINDS = ["CONTRACT", "AMENDMENT", "ANNEX", "OTHER"] as const;
export type LeaseDocumentKind = (typeof LEASE_DOCUMENT_KINDS)[number];
export const LEASE_DOCUMENT_KIND_LABEL: Record<LeaseDocumentKind, string> = {
  CONTRACT: "Szerződés",
  AMENDMENT: "Szerződésmódosítás",
  ANNEX: "Melléklet",
  OTHER: "Egyéb",
};
/** Feltölthető formátumok: PDF és kép (a HEIC a telefonos fotó). */
export const LEASE_DOCUMENT_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"] as const;
export const LEASE_DOCUMENT_MAX_MB = 25;

export const leaseDocumentSchema = z.object({
  kind: z.enum(LEASE_DOCUMENT_KINDS).default("OTHER"),
  title: z.string().trim().min(2, "Adj neki egy rövid nevet").max(200),
  amendmentId: z.string().uuid().nullable().optional(),
  note: optStr(1000),
});
export type LeaseDocumentInput = z.infer<typeof leaseDocumentSchema>;
